import React, { useState, useEffect } from "react";
import Autosuggest from "react-autosuggest";
import useDelay from "../hooks/useDelay";
import AutosuggestHighlightMatch from "autosuggest-highlight/match";
import AutosuggestHighlightParse from "autosuggest-highlight/parse";
import {Spinner} from "./Loading";

const SmartLookup = props => {
	const [value, setValue] = useState("");
	const [suggestions, setSuggestions] = useState([]);
	const [isFetching, setIsFetching] = useState(false);
	
	const DELAY_BEFORE_LOOKUP = 350; // in milliseconds
	const debouncedSearchTerm = useDelay(value, DELAY_BEFORE_LOOKUP);
	
	const troubleFindingOption = [{
		displayName: "Trouble finding yourself?",
		deptClass: "Try searching by your NetID"
	}];

	useEffect(
     () => {
			const fetchSuggestions = async () => {
				if (!debouncedSearchTerm) return setSuggestions([]);

				setIsFetching(true);

				const response = await props.apiService.lookup(value);
				setIsFetching(false);

				if (!response.ok || !response.users || response.users.length === 0) {
					setSuggestions(troubleFindingOption);
					return;
				}

				setSuggestions(response.users); // Happy path
			}
			fetchSuggestions();
    },
		// eslint-disable-next-line
    [debouncedSearchTerm]
  );

	const onSuggestionsFetchRequested = ({ value }) => {
		if (!value) return setSuggestions([]);
	};

	const getSuggestionValue = suggestion => suggestion.displayName;

	const renderSuggestion = ({ displayName, netid, deptClass }, { query, isHighlighted }) => {
		const nameMatches = AutosuggestHighlightMatch(displayName, query);
		const nameParts = AutosuggestHighlightParse(displayName, nameMatches);
		const netidMatches = AutosuggestHighlightMatch(netid, query);
		const netidParts = AutosuggestHighlightParse(netid, netidMatches);
		const deptMatches = AutosuggestHighlightMatch(deptClass, query);
		const deptParts = AutosuggestHighlightParse(deptClass, deptMatches);

		return (
			<div className={`name-wrapper ${isHighlighted ? "highlight-list-item" : ""}`}>
				<div className="name">
					{nameParts.map(({ highlight, text }, index) => {
						return (
							<span className={`${highlight ? "highlight" : ""}`} key={index}>
								{text}
							</span>
						);
					})}
				</div>
				<span className="user-details">
					<span className="dept">
						{deptParts.map(({ highlight, text }, index) => {
							return (
								<span className={`${highlight ? "highlight" : ""}`} key={index}>
									{text}
								</span>
							);
						})}
					</span>
					<span className="netid">
						{netidParts.map(({ highlight, text }, index) => {
							return (
								<span className={`${highlight ? "highlight" : ""}`} key={index}>
									{text}
								</span>
							);
						})}
					</span>
				</span>
			</div>
		);
	};

	const onSuggestionSelected = (event, { suggestion, method }) => {
		if (method === "enter") {
			event.preventDefault();
		}

		if (suggestion.displayName === "Trouble finding yourself?") {
			event.preventDefault();
			props.setAlertState({
				title: "Trouble finding yourself?",
				message: "Try using your NetID to search instead of your name.",
				type: "info"
			});
			setValue("");
			return;
		}

		props.setUser(suggestion);
	};

	const onChange = (event, { newValue }) => {
		setValue(newValue);
	};

	const inputProps = {
		value,
		onChange,
		autoFocus: true,
		disabled: props.disabled,
		id: 'smart_lookup_input'
	};

	return (
		<>
			<div className="smart-lookup-wrapper">
				{isFetching && <Spinner />}
				<label className="subheading" htmlFor="smart_lookup_input">Enter Name or NetID</label>
				<Autosuggest
					className="smart-lookup-field"
					suggestions={suggestions}
					onSuggestionsFetchRequested={onSuggestionsFetchRequested}
					getSuggestionValue={getSuggestionValue}
					renderSuggestion={renderSuggestion}
					alwaysRenderSuggestions={true}
					onSuggestionSelected={onSuggestionSelected}
					inputProps={inputProps}
					highlightFirstSuggestion={true}
				/>
			</div>
		</>
	);
};

export default SmartLookup;
